/**
 * Calendar page — month grid, week view, agenda, mini-calendar, and manual event CRUD.
 * Best-in-class calendar experience with click-to-add, keyboard navigation,
 * day detail popovers, search, and smooth animations.
 *
 * Depends on window.__calendarData being set by the Razor view.
 */
import { csrfHeaders } from './csrf';
import { toast } from './toast';

/**
 * Default colour for a newly created event. Reads the live accent so a new
 * event matches the tenant/theme instead of the old hardcoded indigo, which
 * leaked an off-palette hue into every dot, pill, and agenda bar.
 */
const CAL_FALLBACK_COLOR = '#0f766e';

function readAccentHex() {
  try {
    const raw = getComputedStyle(document.body).getPropertyValue('--accent-deep').trim()
      || getComputedStyle(document.body).getPropertyValue('--accent').trim();
    if (/^#[0-9a-f]{6}$/i.test(raw)) return raw;
    const m = raw.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (m) {
      return '#' + [m[1], m[2], m[3]]
        .map((v) => Number(v).toString(16).padStart(2, '0')).join('');
    }
  } catch (e) { /* fall through */ }
  return CAL_FALLBACK_COLOR;
}

const CAL_DEFAULT_COLOR = readAccentHex();

/**
 * YIQ perceived-brightness ink pick for an arbitrary user-chosen fill, so a
 * light swatch never renders light text and a dark swatch never renders dark
 * text. Only meaningful for fills we actually control (manual/detected); callers
 * skip imported events, which keep their own fixed ink.
 */
function calcInkForFill(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const value = m[1];
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? '#121a2a' : '#ffffff';
}

function initCalendarPage() {
  'use strict';

  if (!window.__calendarData) return;

  const data = window.__calendarData;
  let events = data.events || [];
  let currentDate = new Date();
  let activeView = 'month';
  let searchQuery = '';

  // ── DOM refs ──
  const grid = document.getElementById('cal-grid');
  const monthTitle = document.getElementById('cal-month-title');
  const agenda = document.getElementById('cal-agenda');
  const monthPanel = document.getElementById('cal-month');
  const weekPanel = document.getElementById('cal-week');
  const emptyState = document.getElementById('cal-empty');
  const modalOverlay = document.getElementById('cal-modal-overlay');
  const form = document.getElementById('cal-event-form');
  const modalTitle = document.getElementById('cal-modal-title');
  const deleteBtn = document.getElementById('cal-modal-delete');
  const eventIdInput = document.getElementById('cal-event-id');
  const tzInput = document.getElementById('cal-timezone');
  const popoverOverlay = document.getElementById('cal-popover-overlay');
  const popover = document.getElementById('cal-popover');
  const miniCal = document.getElementById('cal-mini');
  const upcomingPanel = document.getElementById('cal-upcoming');
  const searchInput = document.getElementById('cal-search');
  const dayPopoverOverlay = document.getElementById('cal-day-popover-overlay');
  const dayPopover = document.getElementById('cal-day-popover');

  // ── Color swatches (radios are the single source of truth — no hidden synced input) ──
  const defaultColorInput = form?.querySelector('[data-cal-color-default] input[name="cal-color"]');
  if (defaultColorInput) defaultColorInput.value = CAL_DEFAULT_COLOR;

  function getCalColor() {
    const checked = form?.querySelector('input[name="cal-color"]:checked');
    return checked ? checked.value : '';
  }

  function setCalColor(hex) {
    if (!form) return;
    const normalized = (hex || '').toLowerCase();
    const legacyLabel = form.querySelector('[data-cal-color-legacy]');
    const legacyInput = legacyLabel?.querySelector('input[name="cal-color"]');
    let matched = false;

    form.querySelectorAll('input[name="cal-color"]').forEach((r) => {
      if (r === legacyInput) return;
      const isMatch = r.value.toLowerCase() === normalized;
      r.checked = isMatch;
      if (isMatch) matched = true;
    });

    if (!legacyLabel || !legacyInput) return;
    if (matched) {
      legacyInput.checked = false;
      legacyLabel.hidden = true;
    } else if (hex) {
      // Off-palette legacy hex (event predates the swatch picker, or was set via
      // the old native color input) — surface it so it survives an unrelated edit.
      legacyInput.value = hex;
      legacyLabel.style.setProperty('--cal-swatch-hex', hex);
      legacyLabel.hidden = false;
      legacyInput.checked = true;
    }
  }

  if (tzInput) {
    try { tzInput.value = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) { tzInput.value = data.timezone; }
  }

  // ── Filters ──
  const filters = { manual: true, detected: true, imported: {} };
  document.querySelectorAll('.cal-filter-check').forEach(cb => {
    const prov = cb.dataset.provider;
    const src = cb.dataset.source;
    if (prov) {
      filters.imported[prov] = true;
      cb.addEventListener('change', () => { filters.imported[prov] = cb.checked; render(); trackEvent('calendar_filter_toggled', { source: prov, enabled: cb.checked }); });
    } else if (src === 'detected') {
      cb.addEventListener('change', () => { filters.detected = cb.checked; render(); trackEvent('calendar_filter_toggled', { source: 'detected', enabled: cb.checked }); });
    } else {
      cb.addEventListener('change', () => { filters.manual = cb.checked; render(); trackEvent('calendar_filter_toggled', { source: 'manual', enabled: cb.checked }); });
    }
  });

  // ── Search ──
  searchInput?.addEventListener('input', debounce((e) => {
    searchQuery = e.target.value.trim().toLowerCase();
    render();
    if (searchQuery) trackEvent('calendar_search', { query_length: searchQuery.length });
  }, 200));

  // ── Dialog focus management ──
  // All three overlays wrap aria-modal dialogs. Opening one remembers the control that
  // launched it; closing hands focus back (same return-focus contract as the person
  // wizard in site.js) — without this, keyboard focus silently falls to <body> and the
  // user restarts from the top of the page.
  let modalOpener = null;
  let dayPopoverOpener = null;
  let popoverOpener = null;

  function openDialogOverlay() {
    return [dayPopoverOverlay, modalOverlay, popoverOverlay].find(o => o && !o.hidden) || null;
  }

  // The grid, agenda, and sidebar re-render after every save, destroying the element that
  // opened the dialog — keep a selector (stable class + data anchor) so focus can land on
  // the rebuilt equivalent instead of dropping to <body>.
  function captureOpener() {
    const el = document.activeElement;
    if (!el || el === document.body || !(el instanceof Element)) return null;
    let sel = null;
    if (el.id) {
      sel = '#' + CSS.escape(el.id);
    } else if (el.classList.length > 0) {
      const cls = CSS.escape(el.classList[0]);
      if (el.dataset.date) sel = `.${cls}[data-date="${el.dataset.date}"]`;
      else if (el.dataset.eventId) sel = `.${cls}[data-event-id="${CSS.escape(el.dataset.eventId)}"]`;
    }
    return { el, sel };
  }

  function restoreOpenerFocus(opener, closedOverlay) {
    const target = opener && opener.el && opener.el.isConnected
      ? opener.el
      : (opener && opener.sel ? document.querySelector(opener.sel) : null);
    if (target) { target.focus({ preventScroll: true }); return; }
    // No surviving opener (e.g. its event was just deleted). The overlays hide via CSS,
    // not display:none, so the browser won't blur a control inside one — and a Tab from
    // there would walk the invisible dialog. Park focus on a stable control instead.
    if (closedOverlay && closedOverlay.contains(document.activeElement)) {
      const fallback = document.getElementById('cal-btn-new');
      if (fallback) fallback.focus({ preventScroll: true });
      // The fallback itself can be unfocusable (display:none at mobile widths) — never
      // leave focus inside the closed overlay.
      if (closedOverlay.contains(document.activeElement)) document.activeElement.blur();
    }
  }

  function focusDialog(dialogEl) {
    if (!dialogEl) return;
    dialogEl.setAttribute('tabindex', '-1');
    dialogEl.focus({ preventScroll: true });
  }

  function focusableIn(overlay) {
    return Array.from(overlay.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'))
      .filter(el => !el.disabled && el.type !== 'hidden'
        && el.getAttribute('tabindex') !== '-1' && !el.closest('[hidden]'));
  }

  function trapDialogTab(e, overlay) {
    const items = focusableIn(overlay);
    if (items.length === 0) { e.preventDefault(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!overlay.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }
    // idx -1 = focus is on the dialog element itself (tabindex="-1"): treat as before-first.
    const idx = items.indexOf(active);
    if (e.shiftKey && idx <= 0) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && idx === items.length - 1) {
      e.preventDefault();
      first.focus();
    }
  }

  // ── Keyboard shortcuts ──
  document.addEventListener('keydown', (e) => {
    // Don't capture when typing in inputs
    const tag = e.target.tagName;
    const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable;

    if (e.key === 'Escape') {
      if (dayPopoverOverlay && !dayPopoverOverlay.hidden) { closeDayPopover(); e.preventDefault(); return; }
      if (modalOverlay && !modalOverlay.hidden) { closeModal(); e.preventDefault(); return; }
      if (popoverOverlay && !popoverOverlay.hidden) { closePopover(); e.preventDefault(); return; }
      if (isInput && searchInput === e.target) { searchInput.blur(); searchInput.value = ''; searchQuery = ''; render(); e.preventDefault(); return; }
    }

    if (e.key === 'Tab') {
      const overlay = openDialogOverlay();
      if (overlay) trapDialogTab(e, overlay);
      return;
    }

    if (isInput) return;

    // Page-level shortcuts stay inert while a dialog is open — "n" from a modal button
    // must not stack a second modal, and arrows must not re-render the grid underneath.
    if (openDialogOverlay()) return;

    switch (e.key) {
      case 'n':
      case 'N':
        if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); openModal(null); }
        break;
      case 't':
      case 'T':
        if (!e.ctrlKey && !e.metaKey) { e.preventDefault(); goToToday(); }
        break;
      case '/':
        e.preventDefault();
        searchInput?.focus();
        break;
      case '1':
        if (!e.ctrlKey && !e.metaKey) { switchView('month'); }
        break;
      case '2':
        if (!e.ctrlKey && !e.metaKey) { switchView('week'); }
        break;
      case '3':
        if (!e.ctrlKey && !e.metaKey) { switchView('agenda'); }
        break;
      case 'ArrowLeft':
        if (activeView === 'month') { e.preventDefault(); navigatePrev(); }
        else if (activeView === 'week') { e.preventDefault(); navigatePrev(); }
        break;
      case 'ArrowRight':
        if (activeView === 'month') { e.preventDefault(); navigateNext(); }
        else if (activeView === 'week') { e.preventDefault(); navigateNext(); }
        break;
    }
  });

  function filteredEvents() {
    let result = events.filter(e => {
      if (e.source === 'manual') return filters.manual;
      if (e.source === 'detected') return filters.detected;
      if (e.source === 'imported') return filters.imported[e.provider] !== false;
      return true;
    });
    if (searchQuery) {
      result = result.filter(e => {
        const text = `${e.title} ${e.description || ''} ${e.category || ''} ${e.location || ''}`.toLowerCase();
        return text.includes(searchQuery);
      });
    }
    return result;
  }

  // ── View switcher ──
  function switchView(view) {
    activeView = view;
    document.querySelectorAll('.cal-view-tab').forEach(t => {
      t.classList.toggle('cal-view-tab--active', t.dataset.view === view);
      t.setAttribute('aria-selected', t.dataset.view === view ? 'true' : 'false');
    });
    if (monthPanel) monthPanel.hidden = view !== 'month';
    if (weekPanel) weekPanel.hidden = view !== 'week';
    if (agenda) agenda.hidden = view !== 'agenda';
    render();
    trackEvent('calendar_view_changed', { view_type: view });
  }

  document.querySelectorAll('.cal-view-tab').forEach(tab => {
    tab.addEventListener('click', () => switchView(tab.dataset.view));
  });

  // ── Navigation ──
  function getViewRange() {
    if (activeView === 'week') {
      const start = getWeekStart(currentDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { start: fmtDate(start), end: fmtDate(end) };
    }
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const start = new Date(year, month, 1);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(year, month + 1, 0);
    end.setDate(end.getDate() + (6 - end.getDay()));
    return { start: fmtDate(start), end: fmtDate(end) };
  }

  function navigatePrev() {
    if (activeView === 'week') {
      currentDate.setDate(currentDate.getDate() - 7);
    } else {
      currentDate.setMonth(currentDate.getMonth() - 1);
    }
    const range = getViewRange();
    ensureEventsLoaded(range.start, range.end);
    render();
    renderMiniCalendar();
  }

  function navigateNext() {
    if (activeView === 'week') {
      currentDate.setDate(currentDate.getDate() + 7);
    } else {
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    const range = getViewRange();
    ensureEventsLoaded(range.start, range.end);
    render();
    renderMiniCalendar();
  }

  function goToToday() {
    currentDate = new Date();
    const range = getViewRange();
    ensureEventsLoaded(range.start, range.end);
    render();
    renderMiniCalendar();
  }

  document.getElementById('cal-prev')?.addEventListener('click', navigatePrev);
  document.getElementById('cal-next')?.addEventListener('click', navigateNext);
  document.getElementById('cal-today')?.addEventListener('click', goToToday);

  // ── Render orchestrator ──
  function render() {
    const visible = filteredEvents();
    if (activeView === 'month') renderMonth(visible);
    else if (activeView === 'week') renderWeek(visible);
    else renderAgenda(visible);
    toggleEmpty(visible.length === 0 && events.length === 0);
    renderUpcoming();
    updateNavTitle();
  }

  function updateNavTitle() {
    if (!monthTitle) return;
    if (activeView === 'week') {
      const start = getWeekStart(currentDate);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      if (start.getMonth() === end.getMonth()) {
        monthTitle.textContent = start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      } else if (start.getFullYear() === end.getFullYear()) {
        monthTitle.textContent = `${start.toLocaleDateString(undefined, { month: 'short' })} – ${end.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`;
      } else {
        monthTitle.textContent = `${start.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`;
      }
    } else {
      monthTitle.textContent = new Date(currentDate.getFullYear(), currentDate.getMonth()).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }
  }

  // ── Month view ──
  function renderMonth(visibleEvents) {
    if (!grid) return;
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDay = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const todayStr = fmtDate(today);

    // Previous month fill days
    const prevMonthDays = new Date(year, month, 0).getDate();

    let html = '<div class="cal-grid-header">';
    ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].forEach(d => { html += `<div class="cal-grid-dow">${d}</div>`; });
    html += '</div><div class="cal-grid-body">';

    // Previous month padding cells
    for (let i = startDay - 1; i >= 0; i--) {
      const d = prevMonthDays - i;
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      const dateStr = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayEvents = getEventsForDate(visibleEvents, dateStr);
      html += buildDayCell(d, dateStr, false, false, dayEvents, true);
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isToday = dateStr === todayStr;
      const dayEvents = getEventsForDate(visibleEvents, dateStr);
      html += buildDayCell(d, dateStr, isToday, false, dayEvents, false);
    }

    // Next month padding to fill grid
    const totalCells = startDay + daysInMonth;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let d = 1; d <= remainingCells; d++) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      const dateStr = `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayEvents = getEventsForDate(visibleEvents, dateStr);
      html += buildDayCell(d, dateStr, false, false, dayEvents, true);
    }

    html += '</div>';
    grid.innerHTML = html;
    bindGridEvents();
  }

  function buildDayCell(dayNum, dateStr, isToday, isFocused, dayEvents, isOtherMonth) {
    const MAX_VISIBLE = 3;
    const classes = ['cal-grid-cell'];
    if (isToday) classes.push('cal-grid-cell--today');
    if (isOtherMonth) classes.push('cal-grid-cell--other');
    if (isFocused) classes.push('cal-grid-cell--focused');

    let html = `<div class="${classes.join(' ')}" data-date="${dateStr}" tabindex="0">`;

    // Day number with event dot indicators
    html += `<button type="button" class="cal-grid-day-btn${isToday ? ' cal-grid-day--today' : ''}" data-date="${dateStr}" aria-label="${formatDateForA11y(dateStr)}, ${dayEvents.length} event${dayEvents.length !== 1 ? 's' : ''}">`;
    html += `<span class="cal-grid-day-num">${dayNum}</span>`;
    if (dayEvents.length > 0) {
      html += '<span class="cal-grid-day-dots" aria-hidden="true">';
      const uniqueColors = [...new Set(dayEvents.slice(0, 3).map(e => e.color || (e.source === 'imported' ? '#4285f4' : e.source === 'detected' ? 'var(--detected-accent, #f59e0b)' : 'var(--accent)')))];
      uniqueColors.slice(0, 3).forEach(c => {
        html += `<span class="cal-grid-dot" style="background:${esc(c)}"></span>`;
      });
      html += '</span>';
    }
    html += '</button>';

    // Event chips
    dayEvents.slice(0, MAX_VISIBLE).forEach(e => {
      const hasFill = e.color && e.source !== 'imported';
      const ink = hasFill ? calcInkForFill(e.color) : null;
      const colorStyle = hasFill ? `background:${esc(e.color)};${ink ? `color:${esc(ink)};` : ''}` : '';
      const cls = e.source === 'imported' ? 'cal-evt cal-evt--imported' : e.source === 'detected' ? 'cal-evt cal-evt--detected' : 'cal-evt cal-evt--manual';
      html += `<button type="button" class="${cls}" data-event-id="${esc(e.id)}" title="${esc(e.title)}" style="${colorStyle}">`;
      if (e.isAllDay) {
        html += `<span class="cal-evt-text">${esc(truncate(e.title, 16))}</span>`;
      } else {
        html += `<span class="cal-evt-time">${formatTimeShort(e.startsAt)}</span> `;
        html += `<span class="cal-evt-text">${esc(truncate(e.title, 12))}</span>`;
      }
      html += '</button>';
    });

    if (dayEvents.length > MAX_VISIBLE) {
      html += `<button type="button" class="cal-evt-more" data-date="${dateStr}">+${dayEvents.length - MAX_VISIBLE} more</button>`;
    }

    // Click-to-add area
    html += `<button type="button" class="cal-grid-add" data-date="${dateStr}" aria-label="Add event on ${formatDateForA11y(dateStr)}">`;
    html += '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
    html += '</button>';

    html += '</div>';
    return html;
  }

  function bindGridEvents() {
    if (!grid) return;

    // Event chip clicks
    grid.querySelectorAll('.cal-evt').forEach(el => {
      el.addEventListener('click', (e) => { e.stopPropagation(); onEventClick(el.dataset.eventId); });
    });

    // "+N more" clicks → day detail popover
    grid.querySelectorAll('.cal-evt-more').forEach(el => {
      el.addEventListener('click', (e) => { e.stopPropagation(); openDayPopover(el.dataset.date); });
    });

    // Day number clicks → day detail popover
    grid.querySelectorAll('.cal-grid-day-btn').forEach(el => {
      el.addEventListener('click', (e) => { e.stopPropagation(); openDayPopover(el.dataset.date); });
    });

    // Click-to-add buttons
    grid.querySelectorAll('.cal-grid-add').forEach(el => {
      el.addEventListener('click', (e) => { e.stopPropagation(); openModalForDate(el.dataset.date); });
    });

    // Cell click (empty area) → on mobile open day popover; on desktop dblclick to create event
    grid.querySelectorAll('.cal-grid-cell[data-date]').forEach(el => {
      el.addEventListener('click', (e) => {
        if (e.target.closest('.cal-evt, .cal-evt-more, .cal-grid-day-btn, .cal-grid-add')) return;
        if (window.matchMedia('(max-width: 640px)').matches) openDayPopover(el.dataset.date);
      });
      el.addEventListener('dblclick', (e) => {
        if (e.target.closest('.cal-evt, .cal-evt-more, .cal-grid-day-btn, .cal-grid-add')) return;
        openModalForDate(el.dataset.date);
      });
    });

    // Cell keyboard navigation
    grid.querySelectorAll('.cal-grid-cell[data-date]').forEach(el => {
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openDayPopover(el.dataset.date);
        }
      });
    });
  }

  // ── Week view ──
  function renderWeek(visibleEvents) {
    if (!weekPanel) return;
    const weekStart = getWeekStart(currentDate);
    const today = new Date();
    const todayStr = fmtDate(today);
    const hours = Array.from({ length: 24 }, (_, i) => i);

    let html = '<div class="cal-week-grid">';

    // Header row with day names
    html += '<div class="cal-week-header">';
    html += '<div class="cal-week-gutter cal-week-corner"></div>';
    for (let d = 0; d < 7; d++) {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + d);
      const dateStr = fmtDate(day);
      const isToday = dateStr === todayStr;
      const dayEvents = getEventsForDate(visibleEvents, dateStr);
      html += `<div class="cal-week-day-col${isToday ? ' cal-week-day-col--today' : ''}" data-date="${dateStr}">`;
      html += `<span class="cal-week-day-name">${day.toLocaleDateString(undefined, { weekday: 'short' })}</span>`;
      html += `<button type="button" class="cal-week-day-num${isToday ? ' cal-week-day-num--today' : ''}" data-date="${dateStr}" aria-label="${formatDateForA11y(dateStr)}, ${dayEvents.length} event${dayEvents.length !== 1 ? 's' : ''}">${day.getDate()}</button>`;
      html += '</div>';
    }
    html += '</div>';

    // All-day row
    const allDayEvents = visibleEvents.filter(e => e.isAllDay);
    if (allDayEvents.length > 0) {
      html += '<div class="cal-week-allday">';
      html += '<div class="cal-week-gutter"><span class="cal-week-hour-label">all-day</span></div>';
      for (let d = 0; d < 7; d++) {
        const day = new Date(weekStart);
        day.setDate(day.getDate() + d);
        const dateStr = fmtDate(day);
        const dayAllDay = allDayEvents.filter(e => isEventOnDate(e, dateStr));
        html += `<div class="cal-week-allday-cell" data-date="${dateStr}">`;
        dayAllDay.forEach(e => {
          const cls = e.source === 'imported' ? 'cal-week-evt cal-week-evt--imported' : e.source === 'detected' ? 'cal-week-evt cal-week-evt--detected' : 'cal-week-evt cal-week-evt--manual';
          const hasFill = e.color && e.source !== 'imported';
          const ink = hasFill ? calcInkForFill(e.color) : null;
          const bgStyle = hasFill ? `background:${esc(e.color)};${ink ? `color:${esc(ink)};` : ''}` : '';
          html += `<button type="button" class="${cls}" data-event-id="${esc(e.id)}" style="${bgStyle}">${esc(truncate(e.title, 14))}</button>`;
        });
        html += '</div>';
      }
      html += '</div>';
    }

    // Time grid body
    html += '<div class="cal-week-body" id="cal-week-body">';
    hours.forEach(h => {
      html += '<div class="cal-week-row">';
      html += `<div class="cal-week-gutter"><span class="cal-week-hour-label">${formatHour(h)}</span></div>`;
      for (let d = 0; d < 7; d++) {
        const day = new Date(weekStart);
        day.setDate(day.getDate() + d);
        const dateStr = fmtDate(day);
        const isToday = dateStr === todayStr;
        html += `<div class="cal-week-cell${isToday ? ' cal-week-cell--today' : ''}" data-date="${dateStr}" data-hour="${h}"></div>`;
      }
      html += '</div>';
    });
    html += '</div>';

    // Overlay timed events on top
    html += '</div>';
    weekPanel.innerHTML = html;

    // Place timed events
    const weekBody = document.getElementById('cal-week-body');
    if (weekBody) {
      // Measure the hour-label gutter from the rendered grid rather than assuming the
      // desktop 56px: calendar.css narrows the gutter track to 40px below 768px, and a
      // hardcoded 56 shifted every chip ~16px right on mobile — into the next day's column.
      let weekGutterPx = 56;
      const gutterProbe = weekPanel.querySelector('.cal-week-row, .cal-week-header');
      if (gutterProbe) {
        const firstTrack = parseFloat(getComputedStyle(gutterProbe).gridTemplateColumns);
        if (Number.isFinite(firstTrack) && firstTrack > 0) weekGutterPx = firstTrack;
      }

      const timedEvents = visibleEvents.filter(e => !e.isAllDay);
      timedEvents.forEach(e => {
        const eDate = new Date(e.startsAt);
        const dateStr = fmtDate(eDate);
        const dayIndex = getDayIndexInWeek(weekStart, dateStr);
        if (dayIndex < 0 || dayIndex > 6) return;

        const startHour = eDate.getHours() + eDate.getMinutes() / 60;
        const endDate = e.endsAt ? new Date(e.endsAt) : new Date(eDate.getTime() + 3600000);
        const endHour = endDate.getHours() + endDate.getMinutes() / 60;
        const duration = Math.max(endHour - startHour, 0.5);

        const topPercent = (startHour / 24) * 100;
        const heightPercent = (duration / 24) * 100;
        // Day columns start after the measured hour-label gutter (56px desktop, 40px mobile —
        // see weekGutterPx above) — #cal-week-body itself carries no such offset, so shares
        // must resolve against (100% - gutter), not the full width, or chips drift and bleed
        // across the next day's divider.
        const dayShare = 1 / 7;
        const leftShare = dayIndex * dayShare + dayShare * 0.07;
        const widthShare = dayShare * 0.86;

        const cls = e.source === 'imported' ? 'cal-week-timed cal-week-timed--imported' : e.source === 'detected' ? 'cal-week-timed cal-week-timed--detected' : 'cal-week-timed cal-week-timed--manual';
        const bgColor = e.color && e.source !== 'imported' ? e.color : '';

        const el = document.createElement('button');
        el.type = 'button';
        el.className = cls;
        el.dataset.eventId = e.id;
        el.style.cssText = `top:${topPercent}%;height:${heightPercent}%;left:calc(${weekGutterPx}px + (100% - ${weekGutterPx}px) * ${leftShare});width:calc((100% - ${weekGutterPx}px) * ${widthShare});`;
        if (bgColor) {
          el.style.backgroundColor = bgColor;
          const ink = calcInkForFill(bgColor);
          if (ink) el.style.color = ink;
        }
        el.innerHTML = `<span class="cal-week-timed-time">${formatTimeShort(e.startsAt)}</span><span class="cal-week-timed-title">${esc(truncate(e.title, 20))}</span>`;
        el.addEventListener('click', () => onEventClick(e.id));
        weekBody.appendChild(el);
      });

      // Now-line
      if (isCurrentWeek(weekStart)) {
        const now = new Date();
        const nowPercent = ((now.getHours() + now.getMinutes() / 60) / 24) * 100;
        const dayIdx = now.getDay();
        const nowLine = document.createElement('div');
        nowLine.className = 'cal-week-now';
        nowLine.style.top = `${nowPercent}%`;
        weekBody.appendChild(nowLine);
      }

      // Scroll to ~8am on load
      const scrollTarget = weekBody.querySelector('.cal-week-row:nth-child(8)');
      if (scrollTarget) weekBody.scrollTop = scrollTarget.offsetTop - 20;
    }

    // Bind week view events
    weekPanel.querySelectorAll('.cal-week-cell').forEach(cell => {
      cell.addEventListener('click', () => {
        const dateStr = cell.dataset.date;
        const hour = parseInt(cell.dataset.hour, 10);
        openModalForDateTime(dateStr, hour);
      });
    });

    weekPanel.querySelectorAll('.cal-week-day-num').forEach(el => {
      el.addEventListener('click', () => openDayPopover(el.dataset.date));
    });
  }

  // ── Agenda view ──
  function renderAgenda(visibleEvents) {
    if (!agenda) return;

    // Show 30 days from today for agenda
    const today = new Date();
    const rangeEnd = new Date(today);
    rangeEnd.setDate(rangeEnd.getDate() + 30);

    const grouped = {};
    visibleEvents.forEach(e => {
      const d = toDateString(e.startsAt);
      (grouped[d] = grouped[d] || []).push(e);
    });

    const sortedDays = Object.keys(grouped).sort();
    if (sortedDays.length === 0) {
      agenda.innerHTML = `<div class="cal-agenda-empty">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        <p>No events scheduled</p>
        <button type="button" class="cal-agenda-add-btn" id="cal-agenda-add">Add an event</button>
      </div>`;
      document.getElementById('cal-agenda-add')?.addEventListener('click', () => openModal(null));
      return;
    }

    let html = '';
    sortedDays.forEach(day => {
      const dt = new Date(day + 'T00:00:00');
      const relLabel = relativeDay(dt);
      const isToday = relLabel === 'Today';
      const isPast = dt < new Date(fmtDate(today) + 'T00:00:00') && !isToday;
      const dateText = dt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

      html += `<div class="cal-agenda-day${isPast ? ' cal-agenda-day--past' : ''}">`;
      html += `<div class="cal-agenda-date-row">`;
      html += `<h3 class="cal-agenda-date${isToday ? ' cal-agenda-date-today' : ''}">${dateText}`;
      if (relLabel) html += `<span class="cal-agenda-date-label">${relLabel}</span>`;
      html += '</h3>';
      html += `<button type="button" class="cal-agenda-day-add" data-date="${day}" aria-label="Add event on ${dateText}">`;
      html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
      html += '</button></div>';

      // Sort events: all-day first, then by time
      const sorted = [...grouped[day]].sort((a, b) => {
        if (a.isAllDay && !b.isAllDay) return -1;
        if (!a.isAllDay && b.isAllDay) return 1;
        return new Date(a.startsAt) - new Date(b.startsAt);
      });

      sorted.forEach(e => {
        const isManual = e.source === 'manual';
        const isDetected = e.source === 'detected';
        const isEditable = isManual || isDetected;
        const cls = isDetected ? 'cal-agenda-item cal-agenda-item--detected' : isManual ? 'cal-agenda-item cal-agenda-item--manual' : 'cal-agenda-item cal-agenda-item--imported';
        const time = e.isAllDay ? 'All day' : formatTimeRange(e.startsAt, e.endsAt);
        const colorBar = e.color && isEditable ? `style="border-left-color:${esc(e.color)}"` : '';

        html += `<button type="button" class="${cls}" data-event-id="${esc(e.id)}" ${colorBar}>`;
        html += `<div class="cal-agenda-item-main">`;
        html += `<span class="cal-agenda-time">${time}</span>`;
        html += `<span class="cal-agenda-title">${esc(e.title)}</span>`;
        if (isDetected && e.sourceEmailSender) {
          html += `<span class="cal-agenda-source"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> ${esc(truncate(e.sourceEmailSender, 25))}</span>`;
        }
        if (e.location) {
          html += `<span class="cal-agenda-location"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> ${esc(truncate(e.location, 25))}</span>`;
        }
        html += '</div>';
        html += '<div class="cal-agenda-item-meta">';
        if (isDetected) {
          html += '<span class="cal-badge cal-badge--detected"><svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg> Auto-detected</span>';
        } else if (isManual) {
          html += '<span class="cal-badge cal-badge--manual">My Event</span>';
        } else {
          html += `<span class="cal-badge cal-badge--imported">${esc(e.providerDisplayName || 'Imported')}</span>`;
        }
        if (isDetected && e.sourceEmailMessageId) {
          html += `<a class="cal-agenda-ext" href="/messages/${encodeURIComponent(e.sourceEmailMessageId)}?returnUrl=/calendar" aria-label="View source email" title="View source email"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></a>`;
        }
        if (!isEditable && e.htmlLink) {
          if (/^https?:\/\//i.test(e.htmlLink)) {
          html += `<a class="cal-agenda-ext" href="${esc(e.htmlLink)}" target="_blank" rel="noopener noreferrer" aria-label="View in ${esc(e.providerDisplayName || 'provider')}"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg></a>`;
          }
        }
        html += '</div></button>';
      });
      html += '</div>';
    });

    agenda.innerHTML = html;

    agenda.querySelectorAll('.cal-agenda-item').forEach(el => {
      el.addEventListener('click', (ev) => {
        if (ev.target.closest('.cal-agenda-ext')) return;
        onEventClick(el.dataset.eventId);
      });
    });

    agenda.querySelectorAll('.cal-agenda-day-add').forEach(el => {
      el.addEventListener('click', () => openModalForDate(el.dataset.date));
    });
  }

  function toggleEmpty(show) {
    if (emptyState) emptyState.hidden = !show;
  }

  // ── Mini calendar (sidebar) ──
  function renderMiniCalendar() {
    if (!miniCal) return;
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const today = new Date();
    const todayStr = fmtDate(today);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDay = new Date(year, month, 1).getDay();

    let html = '<div class="cal-mini-nav">';
    html += `<button type="button" class="cal-mini-prev" id="cal-mini-prev" aria-label="Previous month"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg></button>`;
    html += `<span class="cal-mini-title">${new Date(year, month).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>`;
    html += `<button type="button" class="cal-mini-next" id="cal-mini-next" aria-label="Next month"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg></button>`;
    html += '</div>';

    html += '<div class="cal-mini-grid">';
    ['S', 'M', 'T', 'W', 'T', 'F', 'S'].forEach(d => { html += `<span class="cal-mini-dow">${d}</span>`; });

    for (let i = 0; i < startDay; i++) html += '<span class="cal-mini-day cal-mini-day--empty"></span>';

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isToday = dateStr === todayStr;
      const hasEvents = events.some(e => toDateString(e.startsAt) === dateStr);
      const isSelected = activeView === 'month' && fmtDate(currentDate) === dateStr;

      let cls = 'cal-mini-day';
      if (isToday) cls += ' cal-mini-day--today';
      if (hasEvents) cls += ' cal-mini-day--has-events';
      if (isSelected) cls += ' cal-mini-day--selected';

      html += `<button type="button" class="${cls}" data-date="${dateStr}" aria-label="${formatDateForA11y(dateStr)}${hasEvents ? ', has events' : ''}">${d}</button>`;
    }

    html += '</div>';
    miniCal.innerHTML = html;

    // Mini calendar day clicks → navigate main calendar
    miniCal.querySelectorAll('.cal-mini-day[data-date]').forEach(el => {
      el.addEventListener('click', () => {
        currentDate = new Date(el.dataset.date + 'T12:00:00');
        render();
        renderMiniCalendar();
      });
    });

    document.getElementById('cal-mini-prev')?.addEventListener('click', () => {
      currentDate.setMonth(currentDate.getMonth() - 1);
      render();
      renderMiniCalendar();
    });

    document.getElementById('cal-mini-next')?.addEventListener('click', () => {
      currentDate.setMonth(currentDate.getMonth() + 1);
      render();
      renderMiniCalendar();
    });
  }

  // ── Upcoming events (sidebar) ──
  function renderUpcoming() {
    if (!upcomingPanel) return;
    const now = new Date();
    const upcoming = filteredEvents()
      .filter(e => new Date(e.startsAt) >= new Date(fmtDate(now) + 'T00:00:00'))
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
      .slice(0, 5);

    if (upcoming.length === 0) {
      upcomingPanel.innerHTML = '<div class="cal-upcoming-empty-wrap">' +
        '<p class="cal-upcoming-empty">No upcoming events</p>' +
        '<button type="button" class="cal-agenda-add-btn" id="cal-upcoming-add">+ New event</button>' +
        '</div>';
      document.getElementById('cal-upcoming-add')?.addEventListener('click', () => openModal(null));
      return;
    }

    let html = '';
    upcoming.forEach(e => {
      const dateLabel = relativeDay(new Date(toDateString(e.startsAt) + 'T00:00:00'));
      const timeStr = e.isAllDay ? 'All day' : formatTimeShort(e.startsAt);
      const dateStr = dateLabel || new Date(e.startsAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const colorDot = e.color || (e.source === 'imported' ? '#4285f4' : e.source === 'detected' ? 'var(--detected-accent, #f59e0b)' : 'var(--accent)');

      html += `<button type="button" class="cal-upcoming-item" data-event-id="${esc(e.id)}">`;
      html += `<span class="cal-upcoming-dot" style="background:${esc(colorDot)}" aria-hidden="true"></span>`;
      html += '<span class="cal-upcoming-info">';
      html += `<span class="cal-upcoming-title">${esc(truncate(e.title, 22))}</span>`;
      html += `<span class="cal-upcoming-when">${dateStr} · ${timeStr}</span>`;
      html += '</span></button>';
    });

    upcomingPanel.innerHTML = html;

    upcomingPanel.querySelectorAll('.cal-upcoming-item').forEach(el => {
      el.addEventListener('click', () => onEventClick(el.dataset.eventId));
    });
  }

  // ── Day detail popover ──
  function openDayPopover(dateStr) {
    if (!dayPopoverOverlay || !dayPopover) return;
    if (dayPopoverOverlay.hidden) dayPopoverOpener = captureOpener();
    const dayEvents = getEventsForDate(filteredEvents(), dateStr);
    const dt = new Date(dateStr + 'T12:00:00');
    const dateText = dt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const rel = relativeDay(new Date(dateStr + 'T00:00:00'));

    let html = '<div class="cal-daypop-header">';
    // The dialog element declares aria-labelledby="cal-daypop-title" — this id is that target.
    html += `<h3 class="cal-daypop-title" id="cal-daypop-title">${dateText}`;
    if (rel) html += ` <span class="cal-daypop-rel">${rel}</span>`;
    html += '</h3>';
    html += `<button type="button" class="cal-daypop-add" id="cal-daypop-add" data-date="${dateStr}">`;
    html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add';
    html += '</button></div>';

    if (dayEvents.length === 0) {
      html += '<p class="cal-daypop-empty">No events this day</p>';
    } else {
      html += '<div class="cal-daypop-list">';
      // Sort: all-day first, then by time
      const sorted = [...dayEvents].sort((a, b) => {
        if (a.isAllDay && !b.isAllDay) return -1;
        if (!a.isAllDay && b.isAllDay) return 1;
        return new Date(a.startsAt) - new Date(b.startsAt);
      });
      sorted.forEach(e => {
        const isEditable = e.source === 'manual' || e.source === 'detected';
        const isDetected = e.source === 'detected';
        const time = e.isAllDay ? 'All day' : formatTimeRange(e.startsAt, e.endsAt);
        const colorDot = e.color || (e.source === 'imported' ? '#4285f4' : isDetected ? 'var(--detected-accent, #f59e0b)' : 'var(--accent)');
        html += `<button type="button" class="cal-daypop-item${isDetected ? ' cal-daypop-item--detected' : ''}" data-event-id="${esc(e.id)}">`;
        html += `<span class="cal-daypop-dot" style="background:${esc(colorDot)}" aria-hidden="true"></span>`;
        html += '<span class="cal-daypop-item-info">';
        html += `<span class="cal-daypop-item-title">${esc(e.title)}</span>`;
        html += `<span class="cal-daypop-item-time">${time}</span>`;
        if (isDetected) html += '<span class="cal-daypop-item-badge">Auto-detected</span>';
        html += '</span>';
        if (isDetected && e.sourceEmailMessageId) {
          html += `<a class="cal-daypop-source" href="/messages/${encodeURIComponent(e.sourceEmailMessageId)}?returnUrl=/calendar" aria-label="View source email" title="View source email"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></a>`;
        }
        if (isEditable) html += '<span class="cal-daypop-edit" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></span>';
        html += '</button>';
      });
      html += '</div>';
    }

    dayPopover.innerHTML = html;
    dayPopoverOverlay.hidden = false;
    focusDialog(dayPopover);

    // Bind events inside popover
    document.getElementById('cal-daypop-add')?.addEventListener('click', () => {
      closeDayPopover();
      openModalForDate(dateStr);
    });

    dayPopover.querySelectorAll('.cal-daypop-item').forEach(el => {
      el.addEventListener('click', (ev) => {
        if (ev.target.closest('.cal-daypop-source')) return;
        closeDayPopover();
        onEventClick(el.dataset.eventId);
      });
    });
  }

  function closeDayPopover() {
    if (dayPopoverOverlay) dayPopoverOverlay.hidden = true;
    restoreOpenerFocus(dayPopoverOpener, dayPopoverOverlay);
    dayPopoverOpener = null;
  }

  dayPopoverOverlay?.addEventListener('click', (e) => { if (e.target === dayPopoverOverlay) closeDayPopover(); });

  // ── Event click handler ──
  function onEventClick(eventId) {
    const ev = events.find(e => e.id === eventId);
    if (!ev) return;

    if (ev.source === 'manual' || ev.source === 'detected') {
      openModal(ev);
    } else {
      showPopover(ev);
    }
  }

  // ── Modal (create/edit manual event) ──
  function openModal(ev) {
    if (!modalOverlay || !form) return;
    if (modalOverlay.hidden) modalOpener = captureOpener();
    const isEdit = !!ev;
    const isAllDay = isEdit ? ev.isAllDay : false;

    modalTitle.textContent = isEdit ? 'Edit Event' : 'New Event';
    deleteBtn.hidden = !isEdit;
    eventIdInput.value = isEdit ? ev.id : '';

    // Set input type BEFORE setting values so the format matches
    toggleAllDayFields(isAllDay);

    form.querySelector('#cal-title').value = isEdit ? ev.title : '';
    if (isEdit && isAllDay) {
      form.querySelector('#cal-start').value = toDateString(ev.startsAt);
      form.querySelector('#cal-end').value = ev.endsAt ? allDayEndDate(ev) : '';
    } else {
      form.querySelector('#cal-start').value = isEdit ? toLocalInput(ev.startsAt) : '';
      form.querySelector('#cal-end').value = isEdit && ev.endsAt ? toLocalInput(ev.endsAt) : '';
    }
    form.querySelector('#cal-allday').checked = isAllDay;
    form.querySelector('#cal-desc').value = isEdit ? (ev.description || '') : '';
    form.querySelector('#cal-category').value = isEdit ? (ev.category || '') : '';
    setCalColor(isEdit ? (ev.color || CAL_DEFAULT_COLOR) : CAL_DEFAULT_COLOR);

    // Source email link for auto-detected events
    const sourceLink = document.getElementById('cal-source-link');
    if (sourceLink) {
      if (isEdit && ev.sourceEmailMessageId) {
        sourceLink.href = `/messages/${encodeURIComponent(ev.sourceEmailMessageId)}?returnUrl=/calendar`;
        const linkText = document.getElementById('cal-source-link-text');
        if (linkText) linkText.textContent = ev.sourceEmailSender ? `View email from ${ev.sourceEmailSender}` : 'View source email';
        sourceLink.hidden = false;
      } else {
        sourceLink.hidden = true;
      }
    }

    modalOverlay.hidden = false;
    modalOverlay.classList.add('cal-modal-overlay--active');
    form.querySelector('#cal-title').focus();
  }

  function openModalForDate(dateStr) {
    if (!modalOverlay || !form) return;
    if (modalOverlay.hidden) modalOpener = captureOpener();

    modalTitle.textContent = 'New Event';
    deleteBtn.hidden = true;
    eventIdInput.value = '';

    form.querySelector('#cal-title').value = '';
    form.querySelector('#cal-start').value = `${dateStr}T09:00`;
    form.querySelector('#cal-end').value = `${dateStr}T10:00`;
    form.querySelector('#cal-allday').checked = false;
    form.querySelector('#cal-desc').value = '';
    form.querySelector('#cal-category').value = '';
    setCalColor(CAL_DEFAULT_COLOR);

    const sourceLink = document.getElementById('cal-source-link');
    if (sourceLink) sourceLink.hidden = true;

    toggleAllDayFields(false);
    modalOverlay.hidden = false;
    modalOverlay.classList.add('cal-modal-overlay--active');
    form.querySelector('#cal-title').focus();
    trackEvent('calendar_click_to_add', { source: 'day_cell' });
  }

  function openModalForDateTime(dateStr, hour) {
    if (!modalOverlay || !form) return;
    if (modalOverlay.hidden) modalOpener = captureOpener();

    modalTitle.textContent = 'New Event';
    deleteBtn.hidden = true;
    eventIdInput.value = '';

    const hourStr = String(hour).padStart(2, '0');
    const endHour = String(Math.min(hour + 1, 23)).padStart(2, '0');

    form.querySelector('#cal-title').value = '';
    form.querySelector('#cal-start').value = `${dateStr}T${hourStr}:00`;
    form.querySelector('#cal-end').value = `${dateStr}T${endHour}:00`;
    form.querySelector('#cal-allday').checked = false;
    form.querySelector('#cal-desc').value = '';
    form.querySelector('#cal-category').value = '';
    setCalColor(CAL_DEFAULT_COLOR);

    const sourceLink = document.getElementById('cal-source-link');
    if (sourceLink) sourceLink.hidden = true;

    toggleAllDayFields(false);
    modalOverlay.hidden = false;
    modalOverlay.classList.add('cal-modal-overlay--active');
    form.querySelector('#cal-title').focus();
    trackEvent('calendar_click_to_add', { source: 'week_slot' });
  }

  function toggleAllDayFields(isAllDay) {
    const startInput = form?.querySelector('#cal-start');
    const endInput = form?.querySelector('#cal-end');
    if (startInput) startInput.type = isAllDay ? 'date' : 'datetime-local';
    if (endInput) endInput.type = isAllDay ? 'date' : 'datetime-local';
  }

  // All-day checkbox toggle
  form?.querySelector('#cal-allday')?.addEventListener('change', (e) => {
    toggleAllDayFields(e.target.checked);
  });

  function closeModal() {
    resetDeleteConfirm();
    if (modalOverlay) {
      modalOverlay.classList.remove('cal-modal-overlay--active');
      modalOverlay.hidden = true;
    }
    restoreOpenerFocus(modalOpener, modalOverlay);
    modalOpener = null;
  }

  document.getElementById('cal-btn-new')?.addEventListener('click', () => openModal(null));
  document.getElementById('cal-btn-new-empty')?.addEventListener('click', () => openModal(null));
  document.getElementById('cal-modal-close')?.addEventListener('click', closeModal);
  document.getElementById('cal-modal-cancel')?.addEventListener('click', closeModal);
  modalOverlay?.addEventListener('click', (e) => { if (e.target === modalOverlay) closeModal(); });

  // Deep-link support: Home's "+ New event" CTAs land here with ?new=1 so the user opens
  // straight into the create modal instead of a page where they still have to find the button.
  if (new URLSearchParams(window.location.search).get('new') === '1') {
    openModal(null);
  }

  // ── Form submit ──
  const saveBtn = form?.querySelector('button[type="submit"]');
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const eventId = eventIdInput.value;
    const isAllDay = form.querySelector('#cal-allday').checked;
    const startVal = form.querySelector('#cal-start').value;
    const endVal = form.querySelector('#cal-end').value;

    // For all-day events, use exclusive end convention (midnight of next day)
    let startsAtIso, endsAtIso;
    if (isAllDay) {
      startsAtIso = new Date(startVal + 'T00:00:00').toISOString();
      if (endVal) {
        const endDate = new Date(endVal + 'T00:00:00');
        endDate.setDate(endDate.getDate() + 1);
        endsAtIso = endDate.toISOString();
      } else {
        endsAtIso = null;
      }
    } else {
      startsAtIso = new Date(startVal).toISOString();
      endsAtIso = endVal ? new Date(endVal).toISOString() : null;
    }

    const payload = {
      title: form.querySelector('#cal-title').value.trim(),
      startsAt: startsAtIso,
      endsAt: endsAtIso,
      isAllDay: isAllDay,
      description: form.querySelector('#cal-desc').value.trim() || null,
      category: form.querySelector('#cal-category').value.trim() || null,
      color: getCalColor() || null,
      timezoneId: tzInput?.value || null
    };

    if (saveBtn) { saveBtn.disabled = true; saveBtn.classList.add('is-loading'); }
    if (deleteBtn) deleteBtn.disabled = true;

    try {
      let res;
      if (eventId) {
        res = await fetch(`/api/calendar/events/${encodeURIComponent(eventId)}`, {
          method: 'PUT',
          headers: csrfHeaders(),
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const updated = await res.json();
          events = events.map(ev => ev.id === eventId ? updated : ev);
          trackEvent('calendar_event_edited');
        }
      } else {
        res = await fetch('/api/calendar/events', {
          method: 'POST',
          headers: csrfHeaders(),
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const created = await res.json();
          events.push(created);
          trackEvent('calendar_event_created', { has_category: !!payload.category });
        }
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || 'Something went wrong. Please try again.');
        if (saveBtn) { saveBtn.disabled = false; saveBtn.classList.remove('is-loading'); }
        if (deleteBtn) deleteBtn.disabled = false;
        return;
      }

      toast.success(eventId ? 'Event updated' : 'Event created');
      // Re-render BEFORE closing: the opener element dies in the rebuild, and closeModal's
      // focus return needs the rebuilt equivalent to already exist.
      render();
      renderMiniCalendar();
      closeModal();
    } catch (err) {
      console.error('Calendar save error:', err);
      toast.error('Could not save event. Please try again.');
    } finally {
      if (saveBtn) { saveBtn.disabled = false; saveBtn.classList.remove('is-loading'); }
      if (deleteBtn) deleteBtn.disabled = false;
    }
  });

  // ── Delete (inline confirm within modal) ──
  let deleteConfirmPending = false;

  function resetDeleteConfirm() {
    deleteConfirmPending = false;
    if (deleteBtn) {
      deleteBtn.textContent = 'Delete';
      deleteBtn.classList.remove('cal-btn--confirm-delete');
    }
  }

  deleteBtn?.addEventListener('click', async () => {
    const eventId = eventIdInput.value;
    if (!eventId) return;

    if (!deleteConfirmPending) {
      deleteConfirmPending = true;
      deleteBtn.textContent = 'Confirm delete';
      deleteBtn.classList.add('cal-btn--confirm-delete');
      setTimeout(() => { if (deleteConfirmPending) resetDeleteConfirm(); }, 4000);
      return;
    }

    deleteBtn.disabled = true;
    deleteBtn.classList.add('is-loading');
    if (saveBtn) saveBtn.disabled = true;

    try {
      const res = await fetch(`/api/calendar/events/${encodeURIComponent(eventId)}`, { method: 'DELETE', headers: csrfHeaders() });
      if (res.ok || res.status === 404) {
        events = events.filter(e => e.id !== eventId);
        trackEvent('calendar_event_deleted');
        toast.success('Event deleted');
        render();
        renderMiniCalendar();
        closeModal();
      }
    } catch (err) {
      console.error('Calendar delete error:', err);
      toast.error('Could not delete event. Please try again.');
    } finally {
      deleteBtn.disabled = false;
      deleteBtn.classList.remove('is-loading');
      if (saveBtn) saveBtn.disabled = false;
      resetDeleteConfirm();
    }
  });

  // ── Popover (imported events) ──
  function showPopover(ev) {
    if (!popoverOverlay || !popover) return;
    if (popoverOverlay.hidden) popoverOpener = captureOpener();
    document.getElementById('cal-popover-title').textContent = ev.title;

    let meta = ev.providerDisplayName || 'Imported';
    meta += ' · ' + (ev.isAllDay ? 'All day' : formatTimeRange(ev.startsAt, ev.endsAt));
    if (ev.location) meta += ` · ${ev.location}`;
    document.getElementById('cal-popover-meta').textContent = meta;

    document.getElementById('cal-popover-desc').textContent = ev.description || '';
    const link = document.getElementById('cal-popover-link');
    if (ev.htmlLink && /^https?:\/\//i.test(ev.htmlLink)) {
      link.href = ev.htmlLink;
      link.textContent = `View in ${ev.providerDisplayName || 'provider'}`;
      link.hidden = false;
    } else {
      link.hidden = true;
    }
    popoverOverlay.hidden = false;
    focusDialog(popover);
    trackEvent('calendar_imported_event_opened', { provider: ev.provider });
  }

  function closePopover() {
    if (popoverOverlay) popoverOverlay.hidden = true;
    restoreOpenerFocus(popoverOpener, popoverOverlay);
    popoverOpener = null;
  }

  document.getElementById('cal-popover-close')?.addEventListener('click', closePopover);
  popoverOverlay?.addEventListener('click', (e) => { if (e.target === popoverOverlay) closePopover(); });

  // ── Lazy fetch for navigation beyond initial window ──
  const fetchedRanges = [{ start: data.windowStart, end: data.windowEnd }];
  let fetchInProgress = false;

  async function ensureEventsLoaded(start, end) {
    // Check if range already covered
    const isCovered = fetchedRanges.some(r =>
      new Date(r.start) <= new Date(start) && new Date(r.end) >= new Date(end));
    if (isCovered || fetchInProgress) return;

    fetchInProgress = true;
    try {
      const res = await fetch(`/api/calendar/events?from=${encodeURIComponent(start)}&to=${encodeURIComponent(end)}`, {
        headers: { 'Accept': 'application/json' }
      });
      if (res.ok) {
        const newEvents = await res.json();
        // Merge without duplicates
        const existingIds = new Set(events.map(e => e.id));
        newEvents.forEach(e => { if (!existingIds.has(e.id)) events.push(e); });
        fetchedRanges.push({ start, end });
        render();
        renderMiniCalendar();
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      fetchInProgress = false;
    }
  }

  // ── Helpers ──
  function fmtDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  function toDateString(iso) {
    const d = new Date(iso);
    return fmtDate(d);
  }

  /**
   * For all-day events whose endsAt is stored as exclusive (midnight of next day),
   * return the inclusive last day. For non-all-day events, return the date as-is.
   */
  function allDayEndDate(e) {
    if (!e.endsAt) return null;
    const d = new Date(e.endsAt);
    if (e.isAllDay && d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
      d.setDate(d.getDate() - 1);
    }
    return fmtDate(d);
  }

  function toLocalInput(iso) {
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function formatTime(iso) {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }

  function formatTimeShort(iso) {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).replace(/\s/g, '').toLowerCase();
  }

  function formatTimeRange(startIso, endIso) {
    const start = formatTime(startIso);
    if (!endIso) return start;
    const end = formatTime(endIso);
    return `${start} – ${end}`;
  }

  function formatHour(h) {
    if (h === 0) return '12am';
    if (h < 12) return `${h}am`;
    if (h === 12) return '12pm';
    return `${h - 12}pm`;
  }

  function formatDateForA11y(dateStr) {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }

  function getEventsForDate(evts, dateStr) {
    return evts.filter(e => {
      const start = toDateString(e.startsAt);
      if (start === dateStr) return true;
      // Multi-day events
      if (e.endsAt) {
        const end = allDayEndDate(e);
        return dateStr >= start && dateStr <= end;
      }
      return false;
    });
  }

  function isEventOnDate(e, dateStr) {
    const start = toDateString(e.startsAt);
    if (start === dateStr) return true;
    if (e.endsAt) {
      const end = allDayEndDate(e);
      return dateStr >= start && dateStr <= end;
    }
    return false;
  }

  function getWeekStart(date) {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function isCurrentWeek(weekStart) {
    const now = new Date();
    const currentWeekStart = getWeekStart(now);
    return fmtDate(weekStart) === fmtDate(currentWeekStart);
  }

  function getDayIndexInWeek(weekStart, dateStr) {
    // Must match getWeekStart's midnight anchor, not noon: a midnight-vs-noon mismatch adds a
    // constant 0.5-day skew, and Math.round rounds every single day in the week up by one
    // (0.5, 1.5, 2.5, ... all round up) - not a rare edge case, it fired on every event, every day.
    const d = new Date(dateStr + 'T00:00:00');
    const diff = Math.round((d - weekStart) / 86400000);
    return diff;
  }

  function truncate(s, n) { return s.length > n ? s.slice(0, n) + '…' : s; }

  function relativeDay(date) {
    const today = new Date();
    const todayStr = fmtDate(today);
    const dateStr = fmtDate(date);
    if (dateStr === todayStr) return 'Today';
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (fmtDate(tomorrow) === dateStr) return 'Tomorrow';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (fmtDate(yesterday) === dateStr) return 'Yesterday';
    return null;
  }

  function esc(s) {
    if (!s) return '';
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  function debounce(fn, ms) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function trackEvent(name, params) {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, params);
    }
  }

  // ── Init ──
  render();
  renderMiniCalendar();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCalendarPage, { once: true });
} else {
  initCalendarPage();
}
